// The words a reviewer reads on a review item (D86/D93): a plain title per
// reason code, the kind badge, the record and field in doubt, and the rules
// for when Accept is offered. Written for a low-attention reader: what the
// problem is, then what to do.
import { ReviewCandidate, ReviewItemKind, ReviewLineageKind, ReviewReasonCode, ReviewSource } from '@mono/proto-clients';
import { isMoneyField } from '../../lib/correctedValue';
import { formatMoney, optionalTaskLabel, orderCeilingLabel, periodLabel } from '../../lib/recordLabels';
import { reviewSourceProps } from '../../lib/reviewSourceProps';
import { parseSpan } from '../../lib/sourceSpan';
// ---------------------------------------------------------------------------
// Reviewer-facing text: never model labels, scores, raw money, or glossary keys.
// ---------------------------------------------------------------------------
// Verdict/score parentheticals: (WRONG/SAME 0.51), [SAME 0.85], (robin: NEW_TEAM 0.95).
const MODEL_SCORE_PAREN = /\s*[[(](?:robin:\s*)?(?:p=\d+(?:\.\d+)?[^)\]]*|[A-Za-z][A-Za-z0-9_/]*(?:\s*\/\s*[A-Za-z][A-Za-z0-9_/]*)*\s+\d+(?:\.\d+)?)[\])]/gi;
// Standalone ALL_CAPS model labels, optionally "(best answer)".
const MODEL_LABEL_RE = /\b(NO_DUE_TIMING|NEW_TEAM|BASE_SCOPE|ROUTED_TO|PERFORMED|NOT_LISTED|NOT_APPLICABLE|SAME_EFFORT|AMBIGUOUS|SILENT|WAIVED|DUPLICATE|WRONG|SAME|NONE|ABSENT)(?:\s*\(best answer\))?/g;
const MODEL_PLAIN = {
    NO_DUE_TIMING: 'no due timing is stated',
    NEW_TEAM: 'a new team',
    BASE_SCOPE: "the contract's base scope",
    ROUTED_TO: 'routed to another team',
    PERFORMED: 'this team performs it',
    NOT_LISTED: 'not listed by this document',
    NOT_APPLICABLE: 'does not apply',
    SAME_EFFORT: 'the same effort',
    AMBIGUOUS: 'unclear',
    SILENT: 'not mentioned',
    WAIVED: 'waived',
    DUPLICATE: 'duplicates another deliverable',
    WRONG: '',
    SAME: '',
    NONE: '',
    ABSENT: ''
};
export function stripModelInternals(text) {
    let t = text.replace(MODEL_SCORE_PAREN, '');
    t = t.replace(MODEL_LABEL_RE, (_m, code)=>MODEL_PLAIN[code] ?? '');
    t = t.replace(/\bperiod not settled\b/gi, "This document's period of performance is not settled");
    // "Duty id <32-hex>" / bare UUIDs never belong in reviewer copy.
    t = t.replace(/\bDuty\s+id\s+[a-f0-9-]{32,36}\b/gi, 'Deliverable');
    t = t.replace(/\b[a-f0-9]{32}\b/gi, '');
    t = t.replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '');
    // Raw handling tokens left by older summarize paths.
    t = t.replace(/\bHandling\s+performed\b/gi, 'this team performs it');
    t = t.replace(/\bHandling\s+routed\b/gi, 'routed to another team');
    t = t.replace(/\bHandling\s+not_owed\b/gi, 'not owed by this team');
    t = t.replace(/\bperformed\b(?=\s*$|\s*[·|,])/gi, 'this team performs it');
    t = t.replace(/\brouted\b(?=\s*$|\s*[·|,])/gi, 'routed to another team');
    return t.replace(/\s{2,}/g, ' ').replace(/\s+([,.;:!?])/g, '$1')// Drop orphaned commas/colons left by score stripping; keep sentence periods.
    .replace(/^[,:;.\s-]+|[,:;\s-]+$/g, '').replace(/\s*·\s*·/g, ' · ').trim();
}
// "glossary: 4 of 4 sections read" → a human sentence.
export function humanizeGlossaryHealth(text) {
    return text.replace(/\bglossary:\s*(\d+)\s+of\s+(\d+)\s+sections\s+read\b/gi, (_, n, m)=>Number(n) === Number(m) ? `Specra read all ${m} glossary sections` : `Specra read ${n} of ${m} glossary sections`);
}
export function isGlossaryHealthQuote(quote) {
    return /^\s*glossary:\s*\d+\s+of\s+\d+\s+sections\s+read\s*$/i.test(quote.trim());
}
// "unfunded = 27062515.41" and bare funding-table figures
// ("68642534.71 (sum of the rows)") → dollars.
export function formatEmbeddedMoney(text) {
    let t = text.replace(/\b((?:un)?funded(?:_amount)?|total(?:_amount)?|delta|extended_price|unit_price|amount|price)\s*=\s*(-?\d+(?:\.\d+)?)\b/gi, (_, key, n)=>`${key} = ${formatMoney(Number(n))}`);
    t = t.replace(/\b(-?\d{4,}(?:\.\d+)?)\s*(\((?:sum of the rows|sum of the lines|as printed|total minus funded|total plus optional tasks)[^)]*\))/gi, (_, n, paren)=>`${formatMoney(Number(n))} ${paren}`);
    return t;
}
// One pipeline for every string a reviewer reads (titles, reasons, sides, quotes).
export function humanizeReviewerText(text, field = '') {
    if (!text) return text;
    return stripModelInternals(readableDays(humanizeEmbeddedJson(humanizeGlossaryHealth(formatEmbeddedMoney(text)), field)));
}
// Every code has a title; the Record type makes a missing one a type error and
// the copy test makes it a failing test.
export const REASON_TITLES = {
    [ReviewReasonCode.UNSPECIFIED]: 'This needs a look',
    [ReviewReasonCode.CONTRACT_TYPE_CONFLICT]: 'The documents disagree on the contract type',
    [ReviewReasonCode.POP_NOT_READ]: "We couldn't read this line's period of performance",
    [ReviewReasonCode.MOD_EFFECTIVE_DATE_NOT_STATED]: "This modification doesn't state an effective date",
    [ReviewReasonCode.EXERCISE_DATE_NOT_STATED]: "This option exercise doesn't say when it was exercised",
    [ReviewReasonCode.POP_CHANGE_UNTARGETED]: "A period change doesn't say which period it applies to",
    [ReviewReasonCode.SCOPE_NAME_CLASH]: 'Two labor-category names clash, so the scope name is open',
    [ReviewReasonCode.SPLIT_NOT_SUMMING]: "The parts of a split don't add up to the stated total",
    [ReviewReasonCode.MOD_TYPE_UNDER_GATE]: "We're not sure what type of modification this is",
    [ReviewReasonCode.MOD_PURPOSE_UNDER_GATE]: "We're not sure what this modification is for",
    [ReviewReasonCode.MOD_APPLIED_WITHOUT_COMPARISON]: 'This modification was recorded without a line-by-line check',
    [ReviewReasonCode.ADD_EXISTING_LINE_DIFFERS]: 'A modification adds a line that already exists, with different values',
    [ReviewReasonCode.LINE_IDENTITY_UNRESOLVED]: "We can't tell which line this belongs to",
    [ReviewReasonCode.FIELD_SHAPE_INVALID]: "A line or section number doesn't look right",
    [ReviewReasonCode.CLIN_SEQUENCE_GAP]: 'A line number is missing from the sequence',
    [ReviewReasonCode.DANGLING_PWS_REFERENCE]: "A requirement cites a PWS section that isn't in the document",
    [ReviewReasonCode.CITED_ATTACHMENT_ABSENT]: "A requirement cites an attachment we don't have",
    [ReviewReasonCode.LATER_DOCUMENT_CONTRADICTS]: 'A later document disagrees with a value that was already settled',
    [ReviewReasonCode.COMPLETION_DATE_CLAIM_DIFFERS]: "The modification's completion date disagrees with the lines",
    [ReviewReasonCode.PERIOD_FUNDING_UNRESOLVED]: "We can't tell which period this funding belongs to",
    [ReviewReasonCode.OPTION_PERIOD_FUNDED_NOT_EXERCISED]: 'This is funded, but no document says it was exercised',
    [ReviewReasonCode.DOCUMENT_STATES_TWO_VALUES]: 'The document gives two different values for this',
    [ReviewReasonCode.MOD_LINE_SCHEDULE_UNREAD]: "We couldn't read this modification's line schedule",
    [ReviewReasonCode.MOD_OBLIGATION_TOTAL_DIFFERS]: "This modification's funding doesn't add up to the total it states",
    [ReviewReasonCode.MOD_LINE_DELIVERABLES_UNHANDLED]: "This modification's new lines list deliverables we don't schedule from it",
    [ReviewReasonCode.EXERCISE_WINDOW_NOT_STATED]: "This exercise doesn't say which period the task is exercised for",
    [ReviewReasonCode.OCCURRENCE_OWNER_UNRESOLVED]: 'Which team filed this?',
    [ReviewReasonCode.DUE_RULE_UNDER_GATE]: "We're not sure when this deliverable is due",
    [ReviewReasonCode.PROSE_VERB_LINK_UNDECIDED]: "We're not sure this sentence asks for a deliverable",
    [ReviewReasonCode.CROSS_REFERENCE_SUBJECT_DIFFERS]: "A line cites a section that's about something else",
    [ReviewReasonCode.CONDITION_BRANCH_UNRESOLVED]: "The award doesn't say which of its conditions applies",
    [ReviewReasonCode.DUTY_PERIOD_NOT_STATED]: "Deliverables are scheduled for a period the contract doesn't state",
    [ReviewReasonCode.LINE_SNAPSHOT_FROZEN]: 'Lines kept as they were when the award was re-read',
    [ReviewReasonCode.PERIOD_NO_LONGER_IN_DOCUMENT]: 'A period is no longer in the document. Remove it?',
    [ReviewReasonCode.KEEP_DECISION]: 'The document changed after you decided. Keep your decision?',
    [ReviewReasonCode.REVIEWER_ADDED_LINE]: 'A reviewer added a missing line',
    [ReviewReasonCode.REVIEWER_CORRECTED_LINE]: 'A reviewer corrected this line',
    [ReviewReasonCode.OPTIONAL_TASK_NOT_ON_FILE]: "This exercise names an optional task the contract doesn't have",
    [ReviewReasonCode.SOW_SILENT_ON_DUTY]: "The SOW doesn't mention this deliverable",
    [ReviewReasonCode.AWARD_REREAD_SAME_DELIVERABLE]: 'Is this the same deliverable as the one on file?',
    [ReviewReasonCode.AWARD_REREAD_NEW_DELIVERABLE]: 'The award, read again, lists a deliverable that is not on file',
    [ReviewReasonCode.AWARD_REREAD_LINE_SET]: 'The award, read again, lists different lines',
    [ReviewReasonCode.OPTIONAL_TASK_EXERCISE_UNREAD]: "This modification exercises an optional task, but the document doesn't say which",
    [ReviewReasonCode.PWS_CLAUSE_INHERITS_DELIVERABLES]: "These tasks owe another PWS section's deliverables through a clause",
    [ReviewReasonCode.ORDER_CEILING_KIND_UNRESOLVED]: "The document states a ceiling for the whole order, but doesn't say which one",
    [ReviewReasonCode.DELIVERABLE_ROLL_UNREAD]: "The award moves due dates that fall on a weekend or holiday, but doesn't say which way",
    [ReviewReasonCode.OWED_SERIES_UNDATED]: 'A SOW owes this deliverable, but its schedule never starts'
};
export function picksTeam(review) {
    if (review.reasonCode === ReviewReasonCode.SCOPE_NAME_CLASH) return false;
    const field = review.targetFieldPath.trim().toLowerCase();
    const type = review.targetObjectType.trim().toLowerCase();
    // A line naming two efforts (scope_name) is answered with its team(s):
    // each effort, or both, which may be teams this same set creates.
    return field === 'workstream_id' || field === 'team_id' || field === 'scope_name' || type.includes('workstream') || type.includes('team');
}
export function isOccurrenceOwner(review) {
    return review.reasonCode === ReviewReasonCode.OCCURRENCE_OWNER_UNRESOLVED;
}
// Code 26's sources carry each filed due date (YYYY-MM-DD); the deliverable
// name is not on the wire, so the record reads "Filed deliverable" rather than
// the occurrence row id.
export function occurrenceOwnerSummary(review) {
    const dates = review.sources.map((source)=>source.quote.trim()).filter(Boolean).map((quote)=>formatReviewDay(quote) ?? quote);
    const listed = dates.length > 1 ? `${dates.slice(0, -1).join(', ')} and ${dates[dates.length - 1]}` : dates[0];
    return {
        record: 'Filed deliverable',
        dates: listed ?? 'Not stated'
    };
}
// A code whose plain title is written for one kind of record reads wrong on
// another: POP_NOT_READ was a line's, and the period locator (SAD-778) raises
// it on a period too. Those titles name the record instead. LINE_SNAPSHOT_FROZEN
// on period_snapshot is the anti-gutting freeze for vanished periods (SAD-791).
// SAD-908: the two team questions a SOW and an award's product family raise.
export const TEAM_QUESTION_TITLES = {
    sow_team: 'Which team is this document written for?',
    family: 'Same team?',
    identity: 'Which team?'
};
function statedIdentityName(review) {
    if (review.targetLocator.startsWith('name:')) {
        return teamRefLabel(review.targetLocator);
    }
    for (const c of review.candidates){
        const o = parseObject(c.valueJson);
        if (o && hasExactlyKeys(o, [
            'new_team'
        ]) && o.new_team && typeof o.new_team === 'object') {
            const name = o.new_team.name;
            if (typeof name === 'string' && name.trim()) return name.trim();
        }
        if (c.key === 'new_team' && c.label?.trim()) return c.label.trim();
    }
    return '';
}
function teamQuestionTitle(review) {
    if (review.targetObjectType !== 'Workstream') return undefined;
    const field = review.targetFieldPath.trim();
    if (TEAM_QUESTION_TITLES[field] && field !== 'identity') return TEAM_QUESTION_TITLES[field];
    // Naming identity items: state the printed name and the question (SAD-901).
    if (field === 'identity') {
        const name = statedIdentityName(review);
        if (name) return `Which team does “${name}” belong to?`;
        if (review.reason.trim()) {
            return reasonHeadline(humanizeReviewerText(review.reason));
        }
    }
    return TEAM_QUESTION_TITLES[field];
}
function targetTitle(review) {
    const team = teamQuestionTitle(review);
    if (team) return team;
    // A finding with no field of its own (a document-level note) or a missing
    // document is only as specific as its reason: the reason is the title
    // ("Optional Task One: printed unfunded $… ≠ total − funded $…").
    // SOW/EP assignment items similarly carry the real question in the reason
    // ("period not settled…", "no due timing is stated") — never model labels.
    if ((review.targetFieldPath === FINDING_FIELD || review.targetObjectType === 'Document' || review.targetObjectType === 'DutyAssignment') && review.reason.trim()) {
        return reasonHeadline(humanizeReviewerText(review.reason));
    }
    // A date printed two ways: the reason names both printings ("Start printed
    // as 9 Mar 2025, …; page 2 prints 9 Mar 2026"), which is the problem.
    if (review.reasonCode === ReviewReasonCode.DOCUMENT_STATES_TWO_VALUES && review.targetFieldPath.endsWith('_date') && review.reason.trim()) {
        return reasonHeadline(humanizeReviewerText(review.reason));
    }
    // SAD-854: a line's kind is a question about the line, not which line.
    if (review.targetFieldPath === 'line_kind') {
        return "We're not sure what kind of line this is";
    }
    if (review.reasonCode === ReviewReasonCode.POP_NOT_READ && review.targetObjectType === 'PeriodOfPerformance') {
        return `Period of performance not read for ${periodLabel(review.targetLocator)}`;
    }
    if (review.reasonCode === ReviewReasonCode.LINE_SNAPSHOT_FROZEN && review.targetFieldPath === 'period_snapshot') {
        return "The new import didn't propose removing periods";
    }
    if (review.reasonCode === ReviewReasonCode.CONTRACT_TYPE_CONFLICT && !disagrees(review)) {
        return 'No contract type is stated for this line';
    }
    return undefined;
}
const FINDING_FIELD = 'finding';
// The reason's first sentence, as a row title.
function reasonHeadline(reason, max = 140) {
    const t = reason.trim();
    const end = t.search(/\.(\s|$)/);
    const first = end > 0 ? t.slice(0, end) : t;
    return first.length > max ? `${first.slice(0, max - 1)}…` : first;
}
// A review zou could not decode arrives with no kind or code and a reason
// saying so; it reads as "This needs a look" with that reason as the body.
export function reviewTitle(review) {
    const title = targetTitle(review) ?? REASON_TITLES[review.reasonCode] ?? REASON_TITLES[ReviewReasonCode.UNSPECIFIED];
    // Reasons that became titles (period not settled, NO_DUE_TIMING, …) still
    // need model labels and scores stripped (SAD-901).
    return humanizeReviewerText(title);
}
export function kindLabel(kind, targetObjectType = '') {
    if (kind === ReviewItemKind.CONFLICT) return 'Documents disagree';
    if (kind === ReviewItemKind.IDENTITY) {
        if (targetObjectType === 'PeriodOfPerformance') return 'Which period?';
        if (targetObjectType === 'OptionalTask') return 'Which task?';
        if (targetObjectType === 'DutyAssignment') return 'Which deliverable?';
        if (targetObjectType === 'DutyInheritance') return 'Which section?';
        if (targetObjectType === 'SectionList') return 'Still owed?';
        // Code 26 targets the filed Occurrence; its question is the team.
        if (targetObjectType === 'Workstream' || targetObjectType === 'Team' || targetObjectType === 'Occurrence') {
            return 'Which team?';
        }
        return 'Which line?';
    }
    return 'Needs a look';
}
// A conflict is a disagreement only when the documents give two values; one
// with fewer (no type stated, a date not read) is a value nobody stated.
export function disagrees(review) {
    return review.candidates.length >= 2;
}
const NOT_STATED_CODES = new Set([
    ReviewReasonCode.MOD_EFFECTIVE_DATE_NOT_STATED,
    ReviewReasonCode.EXERCISE_DATE_NOT_STATED,
    ReviewReasonCode.EXERCISE_WINDOW_NOT_STATED,
    ReviewReasonCode.DUTY_PERIOD_NOT_STATED
]);
const NOT_SURE_CODES = new Set([
    ReviewReasonCode.MOD_TYPE_UNDER_GATE,
    ReviewReasonCode.MOD_PURPOSE_UNDER_GATE,
    ReviewReasonCode.DUE_RULE_UNDER_GATE,
    ReviewReasonCode.PROSE_VERB_LINK_UNDECIDED
]);
const LOOKS_WRONG_CODES = new Set([
    ReviewReasonCode.FIELD_SHAPE_INVALID,
    ReviewReasonCode.CLIN_SEQUENCE_GAP,
    ReviewReasonCode.DANGLING_PWS_REFERENCE,
    ReviewReasonCode.CROSS_REFERENCE_SUBJECT_DIFFERS
]);
// The badge a reviewer reads first: what kind of problem this is, in the
// reason code's own words. "Documents disagree" only where two values are on
// the table.
export function badgeLabel(review) {
    if (review.kind === ReviewItemKind.IDENTITY) {
        return kindLabel(review.kind, review.targetObjectType);
    }
    const code = review.reasonCode;
    if (NOT_STATED_CODES.has(code)) return 'Not stated';
    if (NOT_SURE_CODES.has(code)) return 'Not sure';
    if (LOOKS_WRONG_CODES.has(code)) return 'Looks wrong';
    if (CHECK_ONLY_CODES.has(code)) return 'Check it';
    switch(code){
        case ReviewReasonCode.POP_NOT_READ:
            return 'Not confirmed';
        case ReviewReasonCode.DOCUMENT_STATES_TWO_VALUES:
            return 'Two values printed';
        case ReviewReasonCode.SPLIT_NOT_SUMMING:
            return "Doesn't add up";
        case ReviewReasonCode.CONDITION_BRANCH_UNRESOLVED:
            return 'Which applies?';
        case ReviewReasonCode.OPTIONAL_TASK_NOT_ON_FILE:
        case ReviewReasonCode.OPTIONAL_TASK_EXERCISE_UNREAD:
            return 'Which task?';
        case ReviewReasonCode.ORDER_CEILING_KIND_UNRESOLVED:
            return 'Which ceiling?';
        case ReviewReasonCode.LINE_SNAPSHOT_FROZEN:
            return 'For your information';
        case ReviewReasonCode.REVIEWER_ADDED_LINE:
        case ReviewReasonCode.REVIEWER_CORRECTED_LINE:
            return 'Reviewer change';
        case ReviewReasonCode.UNSPECIFIED:
            return kindLabel(review.kind, review.targetObjectType);
        default:
            return disagrees(review) ? 'Documents disagree' : 'Not stated';
    }
}
export function kindKey(kind) {
    if (kind === ReviewItemKind.CONFLICT) return 'conflict';
    if (kind === ReviewItemKind.IDENTITY) return 'identity';
    return 'unknown';
}
// "CLIN 0001" / "Modification P00003" / "Contract" / "Option Period 2".
export function recordLabel(type, locator) {
    const l = locator.trim();
    switch(type){
        case 'ContractCLIN':
        case 'CLIN':
            return l ? `CLIN ${l}` : 'A contract line (not yet known)';
        case 'ProgramModification':
            return l ? `Modification ${l}` : 'A modification with no number';
        case 'Program':
            return 'Contract';
        case 'PeriodOfPerformance':
            return l ? periodLabel(l) : 'A period (not yet known)';
        case 'OptionalTask':
            return l ? optionalTaskLabel(l) : 'An optional task (not yet known)';
        case 'DutyCondition':
            return l ? `PWS ${l} conditions` : 'A PWS condition';
        case 'DutyInheritance':
            {
                // "5.1.4@1,2,3": the cited section and the optional tasks citing it.
                const [section, tasks] = l.split('@');
                return section && tasks ? `PWS ${section} for Optional Task${tasks.includes(',') ? 's' : ''} ${tasks.split(',').join(', ')}` : 'A PWS clause';
            }
        case 'SectionList':
            // R9-L, R10-B: the program's one question, answered per period (the
            // locator is the period); its sources name the sections.
            return 'Deliverables a SOW does not list';
        case 'ContractEvent':
            // R10-E: an event the evidence records date on different days.
            return l ? `The ${l.replace(/_/g, ' ')} event` : 'An event';
        case 'OrderCeiling':
            return l ? orderCeilingLabel(l) : 'An order ceiling (kind not stated)';
        case 'Workstream':
            // A SOW's team question names its document; a family question its two
            // products; a name: ref is a staged team's printed name (SAD-914).
            if (l.startsWith('document:')) return l.slice('document:'.length) || 'A document';
            if (l.startsWith('family:')) return l.slice('family:'.length).split('|').join(' and ');
            if (l.startsWith('name:')) return teamRefLabel(l) || 'A team';
            return l ? `Workstream ${l}` : 'Workstream';
        case 'DutyAssignment':
            // SOW handling items locate a deliverable by duty key; prefer the
            // title from targetNames (memberLine / recordLabelFor) when present.
            // (SAD-898 upload path; SAD-901 Choose names the deliverable from the candidate.)
            return l || 'A deliverable';
        default:
            // Never show a bare 32-hex duty id as the record name (SAD-901).
            if (/^[a-f0-9]{32}$/i.test(l) || /^[0-9a-f-]{36}$/i.test(l)) {
                return type === 'ContractDuty' ? 'Deliverable' : type || 'Contract';
            }
            return l ? `${type} ${l}` : type || 'Contract';
    }
}
const FIELD_LABELS = {
    contract_type: 'Contract type',
    funded_amount: 'Funded amount',
    total_amount: 'Total amount',
    pop_start_date: 'Period of performance start',
    pop_end_date: 'Period of performance end',
    start_date: 'Start date',
    end_date: 'End date',
    effective_date: 'Effective date',
    exercised: 'Exercised',
    modification_type: 'Modification type',
    purpose: 'Purpose',
    optional_task_number: 'Optional task',
    clin: 'CLIN family',
    funded_amount_contribution: 'Funding',
    sow_team: 'Team for this document',
    family: 'Same team?',
    identity: 'Team',
    priced_duty: 'Priced duty',
    line_set: 'Contract lines',
    same_duty: 'Same deliverable',
    schedule_rule: 'Schedule rule',
    assignment: 'Assignment',
    handling: 'Handling',
    duty_key: 'CLIN',
    source_lines: 'CLIN',
    pws_ref: 'PWS'
};
// "funded_amount" → "Funded amount"; an empty field is the whole record.
export function fieldLabel(field) {
    const f = field.trim();
    if (!f) return 'The whole record';
    return FIELD_LABELS[f] ?? f.replace(/_/g, ' ').replace(/^\w/, (c)=>c.toUpperCase());
}
// The row id a reviewer's target was stamped with. zou writes the locator as
// {fieldPath: objectId} (targetToLocatorJSON), so that named key is the row.
export function targetRowId(d) {
    try {
        const loc = JSON.parse(d.targetRecordLocatorJson || '{}');
        const v = loc[d.review?.targetFieldPath ?? ''];
        return typeof v === 'string' ? v : '';
    } catch  {
        return '';
    }
}
export const NO_TARGET_NAMES = new Map();
export function targetName(r, names = NO_TARGET_NAMES) {
    // ContractDuty (award schedule) and DutyAssignment (SOW handling) both
    // locate a deliverable by its duty key so the list can show its title.
    if (r.targetObjectType !== 'ContractDuty' && r.targetObjectType !== 'DutyAssignment') {
        return '';
    }
    return names.get(r.targetLocator.trim())?.trim() ?? '';
}
// The record a review item is on, named from the program's own rows where
// they are loaded. A period's locator is the natural key pangaea derived from
// the row, and a period added by hand with no option number derives as
// "base" whatever its type and name say — so a period is named by its row,
// the way the Periods table names it, and the key is only the fallback.
export function recordLabelFor(d, periods = [], names = NO_TARGET_NAMES) {
    const r = d.review;
    if (isOccurrenceOwner(r)) return occurrenceOwnerSummary(r).record;
    // SAD-887: a deliverable is named by its title, never its duty key.
    const named = targetName(r, names);
    if (named) return named;
    // SOW assignment: prefer the title the candidate carries over the SOW id.
    if (r.targetObjectType === 'DutyAssignment') {
        for (const c of r.candidates){
            const o = parseObject(c.valueJson);
            if (!o) continue;
            if (typeof o.title_as_stated === 'string' && o.title_as_stated.trim()) {
                return o.title_as_stated.trim();
            }
            const wp = o.work_product;
            if (wp && typeof wp === 'object' && !Array.isArray(wp)) {
                const title = wp.title;
                if (typeof title === 'string' && title.trim()) return title.trim();
            }
        }
        // Reason / quote often name the deliverable when candidates lack a title.
        const fromReason = reasonHeadline(humanizeReviewerText(r.reason)).replace(/\?$/, '');
        const named = /do with\s+(.+)$/i.exec(fromReason)?.[1] || /deliverable is this\s*[—-]\s*(.+)$/i.exec(fromReason)?.[1] || '';
        if (named.trim() && !/^this deliverable$/i.test(named.trim())) {
            return named.trim();
        }
        const quote = r.sources.map((s)=>s.quote.trim()).find(Boolean);
        if (quote && !isGlossaryHealthQuote(quote) && quote.length < 120) {
            return quote;
        }
    }
    // Naming identity: the printed team name, never a bare "A team".
    if (r.targetObjectType === 'Workstream' && r.targetFieldPath.trim() === 'identity') {
        const name = statedIdentityName(r);
        if (name) return name;
    }
    if (targetKind(r.targetObjectType) === 'period' && periods.length > 0) {
        // The stamped target is a row id once a reviewer picked it, but an
        // extraction may have left a natural key ("base", "option:2") there —
        // pangaea's resolvePoPTarget reads both — so both resolve as keys.
        const rowId = periodIdForKey(targetRowId(d), periods) || periodIdForKey(r.targetLocator, periods);
        const row = rowId ? periods.find((p)=>p.id === rowId) : undefined;
        if (row?.periodName.trim()) return row.periodName.trim();
    }
    return recordLabel(r.targetObjectType, r.targetLocator);
}
// The row subtitle: "CLIN 0001 · Contract type".
export function targetSummary(d, periods = [], names = NO_TARGET_NAMES) {
    const record = recordLabelFor(d, periods, names);
    if (isOccurrenceOwner(d.review)) return `${record} · Due ${occurrenceOwnerSummary(d.review).dates}`;
    const field = d.review.targetFieldPath;
    if (field === FINDING_FIELD) {
        // "Contract · Finding" says nothing; where it was read does.
        const page = firstPage(d.review.sources);
        return page > 0 ? `${record} · page ${page}` : record;
    }
    return field ? `${record} · ${fieldLabel(field)}` : record;
}
export function targetKind(type) {
    switch(type){
        case 'PeriodOfPerformance':
            return 'period';
        case 'ContractCLIN':
        case 'CLIN':
            return 'line';
        case 'OptionalTask':
            return 'task';
        default:
            return null;
    }
}
export function canAssignTarget(type) {
    return targetKind(type) !== null;
}
// Whether settling an identity item means picking a row. A line's optional
// task (LINE_IDENTITY_UNRESOLVED on optional_task_number, K9b-2) is answered
// with a value on that line instead: one of its family's tasks, or "not a
// task", picked like any other candidate.
export function picksTarget(review) {
    if (review.kind !== ReviewItemKind.IDENTITY) return false;
    // SAD-979: "same deliverable?" is answered by its two candidates.
    if (review.reasonCode === ReviewReasonCode.AWARD_REREAD_SAME_DELIVERABLE) return false;
    if (picksTeam(review)) return false;
    // A SOW's identity questions (which award deliverable a row is, SAD-972; a
    // deliverable the SOW never mentions, SAD-969) are answered by their
    // choices: each choice is the assignment it writes, never a row to pick.
    if (review.targetObjectType === 'DutyAssignment') return false;
    // M4: a clause's inheritance (code 43, or code 29 when the cited section
    // differs) is answered by its sections or "none", never a row.
    if (review.targetObjectType === 'DutyInheritance') return false;
    // R9-L, R10-B: the partial-list question is answered No or Yes, never a row.
    if (review.targetObjectType === 'SectionList') return false;
    return !(targetKind(review.targetObjectType) === 'line' && review.targetFieldPath.trim() === 'optional_task_number');
}
// Settled for the commit gate — the same list as zou's validateCommitInvariants
// and pangaea's refuseOpenReviewItems.
export const SETTLED_STATES = new Set([
    'Accepted',
    'Corrected',
    'Waived',
    'Closed',
    'Applied',
    'Acknowledged'
]);
export function isReviewItem(d) {
    return !!d.review;
}
export function isOpenReviewItem(d) {
    return isReviewItem(d) && !SETTLED_STATES.has(d.resolutionState);
}
export function acceptBlocker(d) {
    const r = d.review;
    const field = fieldLabel(r.targetFieldPath).toLowerCase();
    // zou projects pangaea's settle-write table (reviewitem.SettleWritable) on
    // each item: an accepted item that writes nothing refuses the apply (D87),
    // so Accept is offered only where the wire says the value is written.
    if (!d.reviewSettleWrite) {
        if (isCheckOnly(r)) {
            return {
                kind: 'noSettleWrite',
                message: 'Nothing is written for this. Check it against the document, then close it with a ' + 'reason. Commit stays off until you do.'
            };
        }
        const what = r.targetFieldPath.trim() ? field : 'an answer to this';
        return {
            kind: 'noSettleWrite',
            message: `This can't be applied yet: Specra can't write ${what} back. ` + 'Leave it open (commit stays off) or leave it blank with a reason.'
        };
    }
    if (picksTarget(r)) {
        if (!canAssignTarget(r.targetObjectType)) {
            return {
                kind: 'targetUnavailable',
                message: "Specra can't assign this to a line or task yet. Leave it open (commit stays off) " + 'or leave it blank with a reason.'
            };
        }
        // An identity item's value rides the change item's proposed_value_json;
        // with none there is nothing to write once the target is picked.
        if (!hasJsonValue(d.proposedValueJson)) {
            return {
                kind: 'noValue',
                message: `This can't be applied yet: the documents give Specra no ${field} to write. ` + 'Leave it open (commit stays off) or leave it blank with a reason.'
            };
        }
    }
    return null;
}
// An ordinary change item no writer lands (no_writer on the wire, the
// writable-shape table pangaea's strict apply refuses by — SAD-700): Accept
// and Corrected are not offered; the non-writing decisions remain available.
export const NO_WRITER_NOTE = "Specra can't write this change to the contract; leave it blank or record it by hand.";
export function hasNoWriter(d) {
    return !!d.noWriter && !d.review;
}
// The decisions a change item offers: every one for a writable item; only the
// non-writing decisions for one no writer lands.
export function offeredDecisions(d, all) {
    return hasNoWriter(d) ? all.filter((s)=>s === 'Waived' || s === 'Rejected' || s === 'Escalated') : all;
}
// A no-writer item is settled only by a decision that deliberately writes
// nothing. Accepted/Corrected may predate no_writer projection and must be
// revisited rather than allowing commit to reach strict apply.
export function noWriterNeedsDecision(d) {
    return hasNoWriter(d) && ![
        'Waived',
        'Rejected',
        'Escalated'
    ].includes(d.resolutionState);
}
// What Approve all leaves for the reviewer: the no-writer items it skipped.
export function approveAllSkippedNote(skipped) {
    return `${skipped} item${skipped === 1 ? '' : 's'} skipped: Specra can't write ${skipped === 1 ? 'it' : 'them'} to the contract; leave ${skipped === 1 ? 'it' : 'each'} blank or record ${skipped === 1 ? 'it' : 'them'} by hand.`;
}
export function hasJsonValue(s) {
    if (!s) return false;
    try {
        const v = JSON.parse(s);
        return v !== null && v !== '';
    } catch  {
        return s.trim() !== '';
    }
}
// Legacy sentinel: never render this phrase (SAD-901 live check). Prefer a
// labelled reading or a short scalar summary instead.
export const STRUCTURED_VALUE = 'Structured value';
// Natural period keys only ("base", "option:N") — never invent names for ids.
function naturalPeriodLabel(raw) {
    const k = raw.trim().toLowerCase();
    if (k === 'base') return periodLabel(raw);
    if (/^option:\d+$/.test(k)) return periodLabel(raw);
    return null;
}
function parseObject(json) {
    try {
        const v = JSON.parse(json);
        return typeof v === 'object' && v !== null && !Array.isArray(v) ? v : null;
    } catch  {
        return null;
    }
}
function hasExactlyKeys(o, keys) {
    const have = Object.keys(o);
    return have.length === keys.length && keys.every((k)=>k in o);
}
// A staged-team ref ("name:Quantum") is the team's printed name; a live id
// is left as-is (the picker carries a label for those) (SAD-914).
export function teamRefLabel(ref) {
    const t = ref.trim();
    return t.startsWith('name:') ? t.slice('name:'.length).trim() : t;
}
// A schedule-rule conflict candidate (pangaea's modRuleDecision): keep the
// rule on file, or take the modification's rule fields.
function formatRuleDecision(o) {
    if (typeof o.rule_id !== 'string') return null;
    const keys = Object.keys(o);
    if (!keys.every((k)=>k === 'rule_id' || k === 'keep' || k === 'rule')) return null;
    if (o.keep === true && !('rule' in o)) {
        return [
            {
                label: 'Decision',
                value: 'Keep the rule on file'
            }
        ];
    }
    const rule = o.rule;
    if (!rule || typeof rule !== 'object' || Array.isArray(rule)) return null;
    const r = rule;
    const rows = [];
    const text = typeof r.rule_text === 'string' ? r.rule_text.trim() : '';
    if (text) rows.push({
        label: 'Rule',
        value: text
    });
    if (typeof r.kind === 'string' && r.kind.trim()) {
        rows.push({
            label: 'Kind',
            value: r.kind.replace(/_/g, ' ')
        });
    }
    if (typeof r.cadence === 'string' && r.cadence.trim()) {
        rows.push({
            label: 'Cadence',
            value: r.cadence.replace(/_/g, ' ')
        });
    }
    if (typeof r.due_on === 'string' && r.due_on.trim()) {
        rows.push({
            label: 'Due',
            value: formatReviewDay(r.due_on) ?? r.due_on
        });
    }
    if (typeof r.source_locator === 'string' && r.source_locator.trim()) {
        rows.push({
            label: 'Source',
            value: r.source_locator.trim()
        });
    }
    return rows.length > 0 ? rows : [
        {
            label: 'Decision',
            value: 'Take the modification’s rule'
        }
    ];
}
// Scalar fields of an object as labelled rows the reviewer can read. Nested
// objects and arrays become short prose, never raw JSON (SAD-901).
// Labels objectAsRows uses when a field has a dedicated reading elsewhere
// (so an extra key on that shape still says "Funding change", not "Funded delta").
const ROW_LABELS = {
    funded_delta: 'Funding change',
    line: 'Line'
};
// A nested object with no dedicated reader: its scalar fields, never JSON and
// never the banned "Structured value" phrase.
function summarizeObject(o) {
    const sow = formatSowAssignment(o);
    if (sow) return sow.map((r)=>`${r.label} ${r.value}`).join(' · ');
    const parts = [];
    for (const [k, v] of Object.entries(o)){
        if (v === null || v === undefined) continue;
        if (k === 'duty_key' || k === 'duty_id' || k === 'replaces_duty_keys' || k === 'routed_to_workstream_id' || k === 'workstream_id') {
            continue;
        }
        if (typeof v === 'string') {
            const t = v.trim();
            if (!t || isOpaqueId(t)) continue;
            parts.push(`${fieldLabel(k)} ${formatReviewDay(t) ?? naturalPeriodLabel(t) ?? humanizeReviewerText(teamRefLabel(t) || t)}`);
            continue;
        }
        if (typeof v === 'number') {
            parts.push(`${fieldLabel(k)} ${isMoneyField(k) || /(?:amount|delta|price|ceiling|funded)/i.test(k) ? formatMoney(v) : v.toLocaleString()}`);
            continue;
        }
        if (typeof v === 'boolean') parts.push(`${fieldLabel(k)} ${v ? 'Yes' : 'No'}`);
    }
    return parts.length ? parts.join(', ') : '—';
}
function arrayElementText(x) {
    if (typeof x === 'string') return teamRefLabel(x) || x;
    if (typeof x === 'number' || typeof x === 'boolean') return String(x);
    if (x && typeof x === 'object' && !Array.isArray(x)) {
        const o = x;
        if (typeof o.rule_text === 'string' && o.rule_text.trim()) return o.rule_text.trim();
        const nested = valueRows(JSON.stringify(o));
        if (nested?.length) return nested.map((r)=>`${r.label} ${r.value}`).join(', ');
        return summarizeObject(o);
    }
    return '—';
}
function joinArray(raw) {
    if (raw.length === 0) return '—';
    const sep = raw.some((x)=>typeof x === 'object' && x !== null) ? '; ' : ', ';
    return raw.map(arrayElementText).join(sep);
}
// AwardPricedDuty on a Modification priced_duty carrier: rules as rule text,
// CLINs from source_lines — never the internal duty_key (SAD-901).
function formatPricedDuty(o) {
    const hasDutyShape = typeof o.duty_key === 'string' || typeof o.title === 'string' || Array.isArray(o.rules) || Array.isArray(o.source_lines);
    if (!hasDutyShape) return null;
    // Avoid hijacking unrelated objects that happen to have a "title".
    if (!('duty_key' in o || 'rules' in o || 'source_lines' in o || 'pws_ref' in o || 'letter' in o)) {
        return null;
    }
    const rows = [];
    if (typeof o.title === 'string' && o.title.trim()) {
        rows.push({
            label: 'Title',
            value: o.title.trim()
        });
    }
    if (Array.isArray(o.rules)) {
        const texts = o.rules.map((r)=>r && typeof r === 'object' && !Array.isArray(r) ? String(r.rule_text ?? '').trim() : '').filter(Boolean);
        if (texts.length) rows.push({
            label: 'Rules',
            value: texts.join('; ')
        });
    }
    if (Array.isArray(o.source_lines)) {
        const lines = o.source_lines.filter((x)=>typeof x === 'string' && x.trim() !== '');
        if (lines.length) rows.push({
            label: 'CLIN',
            value: lines.join(', ')
        });
    } else if (typeof o.letter === 'string' && o.letter.trim()) {
        rows.push({
            label: 'Letter',
            value: o.letter.trim()
        });
    }
    if (typeof o.pws_ref === 'string' && o.pws_ref.trim()) {
        rows.push({
            label: 'PWS',
            value: o.pws_ref.trim()
        });
    }
    return rows.length > 0 ? rows : null;
}
// SOW DutyAssignment candidate (reviewitem.SowAssignmentValue): deliverable
// title and plain handling — never a duty UUID or raw "performed"/"routed".
function formatSowAssignment(o) {
    const handling = typeof o.handling === 'string' ? o.handling.trim() : '';
    const workProduct = o.work_product && typeof o.work_product === 'object' && !Array.isArray(o.work_product) ? o.work_product : null;
    const none = o.none === true;
    if (!handling && !workProduct && !none) return null;
    if (!('duty_id' in o || 'handling' in o || 'work_product' in o || 'none' in o)) return null;
    const rows = [];
    const title = typeof o.title_as_stated === 'string' && o.title_as_stated.trim() || typeof workProduct?.title === 'string' && workProduct.title.trim() || '';
    if (title) rows.push({
        label: 'Deliverable',
        value: title
    });
    if (none) {
        rows.push({
            label: 'Decision',
            value: 'Not a deliverable'
        });
        return rows.length ? rows : [
            {
                label: 'Decision',
                value: 'Not a deliverable'
            }
        ];
    }
    if (workProduct && !handling) {
        rows.push({
            label: 'Decision',
            value: 'A SOW-only work product'
        });
        return rows.length ? rows : [
            {
                label: 'Decision',
                value: 'A SOW-only work product'
            }
        ];
    }
    const handlingPlain = {
        performed: 'This team performs it',
        routed: 'Routed to another team',
        not_owed: 'Not owed by this team'
    };
    if (handling) {
        rows.push({
            label: 'Handling',
            value: handlingPlain[handling] ?? handling.replace(/_/g, ' ')
        });
    }
    return rows.length > 0 ? rows : null;
}
// "Same team?" answer: {family:"one", keep, merge} | {family:"two"}.
function formatFamilyAnswer(o) {
    if (typeof o.family !== 'string') return null;
    if (o.family === 'two' && Object.keys(o).every((k)=>k === 'family')) {
        return [
            {
                label: 'Answer',
                value: 'Two separate teams'
            }
        ];
    }
    if (o.family === 'one') {
        const keep = typeof o.keep === 'string' ? o.keep.trim() : '';
        const merge = typeof o.merge === 'string' ? o.merge.trim() : '';
        const value = keep && merge ? `One team: ${keep} (also called ${merge})` : keep ? `One team: ${keep}` : 'One team owning both products';
        return [
            {
                label: 'Answer',
                value
            }
        ];
    }
    return null;
}
function isOpaqueId(s) {
    const t = s.trim();
    return /^[a-f0-9]{32}$/i.test(t) || /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(t);
}
function objectAsRows(o, field = '') {
    const rows = [];
    for (const [key, raw] of Object.entries(o)){
        // Internal keys / opaque ids are never shown (SAD-901).
        if (key === 'duty_key' || key === 'replaces_duty_keys' || key === 'duty_id' || key === 'routed_to_workstream_id' || key === 'workstream_id') {
            continue;
        }
        const label = ROW_LABELS[key] ?? fieldLabel(key);
        if (raw === null || raw === undefined) continue;
        if (typeof raw === 'string') {
            if (isOpaqueId(raw)) continue;
            rows.push({
                label,
                value: formatReviewDay(raw) ?? naturalPeriodLabel(raw) ?? (raw.trim() === '' ? '—' : humanizeReviewerText(teamRefLabel(raw) || raw, field))
            });
            continue;
        }
        if (typeof raw === 'number') {
            const money = isMoneyField(key) || isMoneyField(field) || /(?:amount|delta|price|ceiling|funded)/i.test(key);
            rows.push({
                label,
                value: money ? formatMoney(raw) : raw.toLocaleString()
            });
            continue;
        }
        if (typeof raw === 'boolean') {
            rows.push({
                label,
                value: raw ? 'Yes' : 'No'
            });
            continue;
        }
        if (Array.isArray(raw)) {
            rows.push({
                label,
                value: joinArray(raw)
            });
            continue;
        }
        if (typeof raw === 'object') {
            const nested = valueRows(JSON.stringify(raw));
            if (nested) {
                for (const row of nested)rows.push({
                    label: `${label} · ${row.label}`,
                    value: row.value
                });
            } else {
                rows.push({
                    label,
                    value: summarizeObject(raw)
                });
            }
        }
    }
    return rows;
}
// The object shapes a review value is known to take, as the rows a reviewer
// reads. Every object falls back to labelled scalar rows so raw JSON is never
// shown (SAD-901). Exact-key shapes win so money and rule cards stay precise.
export function valueRows(json) {
    const o = parseObject(json);
    if (!o) return null;
    // PERIOD_FUNDING_UNRESOLVED: the line and the funding change it would place
    // (pangaea's periodFundingValue).
    if (hasExactlyKeys(o, [
        'line',
        'funded_delta'
    ]) && typeof o.line === 'string' && typeof o.funded_delta === 'number') {
        return [
            {
                label: 'Line',
                value: o.line
            },
            {
                label: 'Funding change',
                value: formatMoney(o.funded_delta)
            }
        ];
    }
    // Settled team creates / picks (SAD-914): never show a raw "name:" ref.
    if (hasExactlyKeys(o, [
        'name',
        'aliases'
    ]) && typeof o.name === 'string') {
        const name = o.name.trim();
        if (!name) return null;
        const aliases = Array.isArray(o.aliases) ? o.aliases.filter((a)=>typeof a === 'string' && a.trim() !== '').map((a)=>a.trim()) : [];
        return aliases.length > 0 ? [
            {
                label: 'Team',
                value: name
            },
            {
                label: 'Also known as',
                value: aliases.join(', ')
            }
        ] : [
            {
                label: 'Team',
                value: name
            }
        ];
    }
    // S5b-2 / SAD-914: Accept {workstream_id} | {workstream_ids} | {new_team:{name}}.
    if (hasExactlyKeys(o, [
        'new_team'
    ]) && o.new_team && typeof o.new_team === 'object') {
        const team = o.new_team;
        const name = typeof team.name === 'string' ? team.name.trim() : '';
        return [
            {
                label: 'New team',
                value: name || '—'
            }
        ];
    }
    if (hasExactlyKeys(o, [
        'workstream_id'
    ]) && typeof o.workstream_id === 'string') {
        const label = teamRefLabel(o.workstream_id);
        return [
            {
                label: 'Team',
                value: label || 'An existing team on the program'
            }
        ];
    }
    if (hasExactlyKeys(o, [
        'workstream_ids'
    ]) && Array.isArray(o.workstream_ids)) {
        const names = o.workstream_ids.filter((id)=>typeof id === 'string').map(teamRefLabel).filter(Boolean);
        if (names.length > 0) return [
            {
                label: 'Teams',
                value: names.join(' + ')
            }
        ];
        const n = o.workstream_ids.length;
        return [
            {
                label: 'Teams',
                value: `${n} team${n === 1 ? '' : 's'} on the program`
            }
        ];
    }
    // A party candidate: {name, role} (finding.go).
    if (typeof o.name === 'string' && typeof o.role === 'string' && Object.keys(o).every((k)=>k === 'name' || k === 'role')) {
        return [
            {
                label: 'Party',
                value: `${o.name} (${o.role})`
            }
        ];
    }
    const sowRows = formatSowAssignment(o);
    if (sowRows) return sowRows;
    const familyRows = formatFamilyAnswer(o);
    if (familyRows) return familyRows;
    const priced = formatPricedDuty(o);
    if (priced) return priced;
    const ruleRows = formatRuleDecision(o);
    if (ruleRows) return ruleRows;
    const rows = objectAsRows(o);
    return rows.length > 0 ? rows : null;
}
// Whether a value is an object or array we still cannot read as labelled rows.
// field lets known shapes that need their path (exercise_window) count as known.
// SAD-901: when valueRows returns rows, this is false — raw JSON is never shown.
export function isUnknownShape(json, field = '') {
    if (isExerciseWindowField(field) && formatExerciseWindow(json)) return false;
    if (valueRows(json)) return false;
    try {
        const v = JSON.parse(json);
        // An array of scalars is readable via displayValue; only nested/object
        // arrays remain unknown, and even those render as STRUCTURED_VALUE, never JSON.
        return typeof v === 'object' && v !== null;
    } catch  {
        return false;
    }
}
const MONTHS_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];
// "6 Jul 2026" from a YYYY-MM-DD day (UTC calendar day, never local).
function formatReviewDay(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const written = new Date(Date.UTC(year, month - 1, day));
    if (written.getUTCFullYear() !== year || written.getUTCMonth() !== month - 1 || written.getUTCDate() !== day) {
        return null;
    }
    return `${day} ${MONTHS_SHORT[month - 1]} ${year}`;
}
// SAD-788: OptionalTask/<n>/exercise_window arrives as
// {"start":"YYYY-MM-DD","end":"YYYY-MM-DD"} (end may be absent). Render a
// range a reviewer can read, not raw JSON.
function formatExerciseWindow(json) {
    const o = parseObject(json);
    if (!o) return null;
    const keys = Object.keys(o);
    if (!keys.every((k)=>k === 'start' || k === 'end')) return null;
    // A boundary that is present but not a string is not a window we can read:
    // leave it to the raw-JSON details view.
    if (keys.some((k)=>typeof o[k] !== 'string')) return null;
    const start = typeof o.start === 'string' ? o.start : '';
    const end = typeof o.end === 'string' ? o.end : '';
    if (!start && !end) return null;
    const startLabel = start ? formatReviewDay(start) : null;
    const endLabel = end ? formatReviewDay(end) : null;
    if (start && !startLabel) return null;
    if (end && !endLabel) return null;
    if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
    if (startLabel) return `from ${startLabel}`;
    if (endLabel) return `until ${endLabel}`;
    return null;
}
function isExerciseWindowField(field) {
    return /(^|\/)exercise_window$/i.test(field.trim());
}
// A JSON value as the reviewer reads it, on one line. Money on a money field
// is always cents ("$4,000,000.40", never "4,000,000.4"). Never raw JSON and
// never the banned "Structured value" phrase (SAD-901).
export function displayValue(json, field = '') {
    if (!json) return '—';
    if (isExerciseWindowField(field)) {
        const window = formatExerciseWindow(json);
        if (window) return window;
    }
    const rows = valueRows(json);
    if (rows) return rows.map((r)=>`${r.label} ${r.value}`).join(' · ');
    try {
        const v = JSON.parse(json);
        if (typeof v === 'number') return isMoneyField(field) ? formatMoney(v) : v.toLocaleString();
        if (typeof v === 'boolean') return v ? 'Yes' : 'No';
        if (typeof v === 'string' && formatReviewDay(v)) return formatReviewDay(v);
        // A line's window ("2026-03-09/2026-10-31"): both ends as days.
        if (typeof v === 'string') {
            if (isOpaqueId(v)) return '—';
            const period = naturalPeriodLabel(v);
            if (period) return period;
            const [start, end] = v.split('/');
            const s = start && formatReviewDay(start);
            const e = end && formatReviewDay(end);
            if (s && e && v.split('/').length === 2) return `${s} – ${e}`;
            return humanizeReviewerText(v, field);
        }
        if (Array.isArray(v)) return joinArray(v);
        if (typeof v === 'object' && v !== null) return summarizeObject(v);
        return String(v);
    } catch  {
        // Plain text that is not JSON — show as written, never invent braces.
        return naturalPeriodLabel(json) ?? humanizeReviewerText(json, field);
    }
}
// The rule text a schedule_rule candidate carries: its label after the
// "On file:" / "Modification:" prefix, or the rule_text inside valueJson.
export function scheduleRuleWords(c) {
    const fromLabel = (c.label ?? '').replace(RULE_SIDE_PREFIX, '').trim();
    if (fromLabel) return fromLabel;
    const o = parseObject(c.valueJson ?? '');
    if (!o) return displayValue(c.valueJson ?? '', 'schedule_rule');
    if (o.keep === true) return 'Keep the rule on file';
    const rule = o.rule;
    if (rule && typeof rule === 'object' && !Array.isArray(rule)) {
        const text = rule.rule_text;
        if (typeof text === 'string' && text.trim()) return text.trim();
    }
    return displayValue(c.valueJson ?? '', 'schedule_rule');
}
const RULE_SIDE_PREFIX = /^(on file|modification):\s*/i;
// How many items still need a decision before commit (zou's open/routed and
// unresolved Blocking gate). The button and the list share this wording.
export function needsDecisionNote(count) {
    return `${count} item${count === 1 ? '' : 's'} need${count === 1 ? 's' : ''} a decision`;
}
// Whether a candidate's valueJson is a team id shape whose reading hides the
// team name — prefer the candidate label instead.
function prefersCandidateLabel(valueJson, field) {
    if (picksTeamField(field)) return true;
    const f = field.trim().toLowerCase();
    if (f === 'assignment' || f === 'identity') return true;
    const o = parseObject(valueJson);
    if (!o) return false;
    return hasExactlyKeys(o, [
        'workstream_id'
    ]) || hasExactlyKeys(o, [
        'workstream_ids'
    ]) || hasExactlyKeys(o, [
        'new_team'
    ]) || typeof o.family === 'string' || typeof o.handling === 'string' || o.work_product != null || o.none === true;
}
function picksTeamField(field) {
    const f = field.trim().toLowerCase();
    return f === 'workstream_id' || f === 'team_id' || f === 'sow_team' || f === 'family' || f === 'identity';
}
// The team name the document printed (new_team candidate), never the first
// alphabetically listed live team.
function documentTeamName(r) {
    for (const c of r.candidates){
        const o = parseObject(c.valueJson);
        if (o && hasExactlyKeys(o, [
            'new_team'
        ]) && o.new_team && typeof o.new_team === 'object') {
            const name = o.new_team.name;
            if (typeof name === 'string' && name.trim()) return name.trim();
            if (c.label?.trim()) return c.label.trim();
        }
        if (c.key === 'new_team' && c.label?.trim()) return c.label.trim();
    }
    if (r.targetLocator.startsWith('name:')) return teamRefLabel(r.targetLocator);
    if (r.targetLocator.startsWith('document:')) {
        return r.targetLocator.slice('document:'.length).trim();
    }
    return '';
}
function sowAssignmentSide(c) {
    const o = parseObject(c.valueJson);
    if (!o) return null;
    const rows = formatSowAssignment(o);
    if (!rows) return null;
    const title = rows.find((r)=>r.label === 'Deliverable')?.value ?? '';
    const handling = rows.find((r)=>r.label === 'Handling')?.value ?? rows.find((r)=>r.label === 'Decision')?.value ?? '';
    if (title && handling) {
        const h = handling.charAt(0).toLowerCase() + handling.slice(1);
        return `${title}: ${h}`;
    }
    if (handling) return handling;
    if (title) return title;
    return rows.map((r)=>r.value).join(' · ');
}
function candidateSideText(c, field) {
    if (field === 'schedule_rule') return scheduleRuleWords(c);
    // SOW assignment: title + plain handling — never "Duty id <hex> · Handling performed".
    const sow = sowAssignmentSide(c);
    if (sow) return sow;
    const label = humanizeReviewerText((c.label ?? '').trim(), field);
    if (label && prefersCandidateLabel(c.valueJson, field)) return label;
    // Funding-table choices carry the figure and how it was read ("sum of the
    // rows" / "as printed"); keep that context once the dollars are formatted.
    if (label && /\((?:sum of the|as printed|total minus|total plus)/i.test(label)) {
        return label;
    }
    const v = displayValue(c.valueJson, field);
    if (v && v !== STRUCTURED_VALUE && v !== '—') return humanizeReviewerText(v, field);
    return label || v;
}
function plainReason(reason, field) {
    return compactReason(humanizeReviewerText(reason, field)).short;
}
export function blockingItemPlain(d, periods = [], names = NO_TARGET_NAMES) {
    if (isReviewItem(d)) {
        const r = d.review;
        const record = recordLabelFor(d, periods, names);
        // Prefer a real document quote over glossary-health basis text.
        const quoteRaw = r.sources.map((s)=>s.quote.trim()).find((q)=>q && !isGlossaryHealthQuote(q)) || r.candidates.flatMap((c)=>c.sources.map((s)=>s.quote.trim())).find(Boolean) || r.sources.map((s)=>s.quote.trim()).find(Boolean) || '';
        const glossaryOnly = !!quoteRaw && isGlossaryHealthQuote(quoteRaw);
        const quote = quoteRaw ? humanizeReviewerText(quoteRaw) : '';
        const field = r.targetFieldPath;
        const teamQ = teamQuestionTitle(r);
        const isTeamCard = !!teamQ || picksTeam(r);
        const docTeam = isTeamCard ? documentTeamName(r) : '';
        const sides = r.candidates.map((c)=>candidateSideText(c, field)).filter(Boolean);
        const reasonPlain = r.reason.trim() ? plainReason(r.reason, field) : '';
        let conflictsWith = '';
        if (isTeamCard) {
            // Never "CAATS vs CAMS" (first two live teams alphabetically).
            conflictsWith = docTeam ? `The document names “${docTeam}”` : reasonPlain || reviewTitle(r);
        } else if (sides.length >= 2) {
            const a = humanizeReviewerText(sides[0], field);
            const b = humanizeReviewerText(sides[1], field);
            if (a === b) {
                conflictsWith = reasonPlain || `Both sides show ${a}; check what actually differs in the document`;
            } else {
                conflictsWith = `“${a}” vs “${b}”`;
            }
        } else if (reasonPlain) {
            conflictsWith = reasonPlain;
        } else {
            conflictsWith = reviewTitle(r);
        }
        let documentSays;
        if (glossaryOnly) {
            documentSays = quote; // sentence, not a quote
        } else if (quote) {
            documentSays = `“${quote}”`;
        } else if (docTeam) {
            documentSays = `The document names “${docTeam}”`;
        } else if (sides[0]) {
            documentSays = `The document states “${humanizeReviewerText(sides[0], field)}”`;
        } else {
            documentSays = 'No passage recorded';
        }
        const choose = isTeamCard ? docTeam ? `${teamQ ?? 'Which team?'} The document names “${docTeam}”.` : teamQ ?? reviewQuestion(r, record, []) : reviewQuestion(r, record, r.candidates.map((c)=>candidateSideText(c, field)).filter(Boolean));
        return {
            id: d.id,
            title: reviewTitle(r),
            documentSays,
            conflictsWith,
            choose
        };
    }
    let field = '';
    try {
        const loc = JSON.parse(d.targetRecordLocatorJson || '{}');
        field = Object.keys(loc)[0] || '';
    } catch  {
        field = '';
    }
    const before = displayValue(d.baselineValueJson, field);
    const after = displayValue(d.proposedValueJson, field);
    return {
        id: d.id,
        title: field ? fieldLabel(field) : 'Change item',
        documentSays: after !== '—' ? `The document now has ${after}` : 'No new value stated',
        conflictsWith: before !== '—' && before === after ? `On file shows the same ${before}; check the arithmetic or what else changed` : before !== '—' ? `On file: ${before}` : 'Nothing on file for this field',
        choose: 'Accept, correct, leave blank, or reject this change'
    };
}
// Replace JSON blobs embedded in emitter reasons (KEEP_DECISION's
// "The document now says {…}") with the same reading displayValue uses.
export function humanizeEmbeddedJson(text, field = '') {
    return text.replace(/(\{[\s\S]*\}|\[[\s\S]*\])/g, (blob)=>{
        try {
            JSON.parse(blob);
            return displayValue(blob, field);
        } catch  {
            return blob;
        }
    });
}
export function firstPage(sources) {
    return sources.find((s)=>s.page > 0)?.page ?? 0;
}
// The audit rationale written for the reviewer, with their optional note.
export function pickRationale(c, note, field = '') {
    const page = firstPage(c.sources);
    return withNote(`Picked "${c.label || displayValue(c.valueJson, field)}"${page ? ` (p.${page})` : ''}`, note);
}
export function enteredRationale(raw, note) {
    return withNote(`Entered ${raw}`, note);
}
export function assignedRationale(targetLabel, note) {
    return withNote(`Assigned to ${targetLabel}`, note);
}
// A date the reviewer filled with the "Use today's date" suggestion.
export function todayRationale(isoDate, note) {
    return withNote(`Reviewer set today's date (${isoDate})`, note);
}
// "Leave blank" is recorded as the Waived resolution, which users never see:
// waiver has its own meaning in contracting. The reason is required.
export const LEFT_BLANK = 'Left blank: not stated';
// An option funded with no exercise on file is answered "Funded, not
// exercised" (D116), stored the same way: nothing is written, the money
// still counts.
export const FUNDED_NOT_EXERCISED = 'Funded, not exercised';
const LEAVE_BLANK_COPY = {
    label: LEFT_BLANK,
    toggle: 'Leave it blank…',
    action: 'Leave blank',
    hint: 'Nothing is written for this field; the item stops blocking commit.',
    prompt: 'Why leave it blank?',
    placeholder: "For example, the document doesn't say.",
    settled: `${LEFT_BLANK}. Nothing is written for this field.`
};
const ONE_FILING_FOR_EVERY_TEAM_COPY = {
    label: 'One filing stands for every team',
    toggle: 'Leave it as one filing for every team…',
    action: 'Leave blank',
    hint: 'The existing filing stands for every team; the item stops blocking commit.',
    prompt: 'Why should one filing stand for every team?',
    placeholder: 'For example, the same filing covers every team.',
    settled: 'One filing stands for every team.'
};
const FUNDED_NOT_EXERCISED_COPY = {
    label: FUNDED_NOT_EXERCISED,
    toggle: `${FUNDED_NOT_EXERCISED}…`,
    action: FUNDED_NOT_EXERCISED,
    hint: 'Nothing is marked exercised. The money still counts, and the item stops blocking commit.',
    prompt: 'Why is it not exercised?',
    placeholder: 'For example, this is incremental funding; no exercise is on file.',
    settled: `${FUNDED_NOT_EXERCISED}. The money counts; nothing is marked exercised.`
};
// A check on the whole modification (its line schedule went unread, or its
// funding doesn't add up to its stated total) has no value to write: the
// reviewer checks it and closes it, stored the same way as a blank.
export const CHECKED = 'Checked';
const CHECKED_COPY = {
    label: CHECKED,
    toggle: 'Close it…',
    action: 'Close',
    hint: 'Nothing is written; the item stops blocking commit.',
    prompt: 'What did you check?',
    placeholder: 'For example, checked the funding lines against the PDF.',
    settled: `${CHECKED}. Nothing is written.`,
    toast: 'Closed'
};
// Every code whose finding has no value to write (its field has no
// settle-write): the reviewer checks it against the document and closes it.
const CHECK_ONLY_CODES = new Set([
    ReviewReasonCode.MOD_LINE_SCHEDULE_UNREAD,
    ReviewReasonCode.MOD_OBLIGATION_TOTAL_DIFFERS,
    ReviewReasonCode.MOD_APPLIED_WITHOUT_COMPARISON,
    ReviewReasonCode.CITED_ATTACHMENT_ABSENT
]);
// What the reviewer does about a finding that has no value to pick: a check
// to make, or a notice to acknowledge.
const NEXT_STEP = {
    [ReviewReasonCode.MOD_LINE_SCHEDULE_UNREAD]: "Open the modification and check its line schedule against this contract's lines. Correct " + 'any line that is off, then close this with what you checked.',
    [ReviewReasonCode.MOD_OBLIGATION_TOTAL_DIFFERS]: 'Open the modification and add up its funding lines against the total it states. Correct any ' + 'line that is off, then close this with what you checked.',
    [ReviewReasonCode.MOD_APPLIED_WITHOUT_COMPARISON]: "Open the modification and check what it changed against this contract's lines and periods. " + 'Correct anything that is off by hand, then close this with what you checked.',
    [ReviewReasonCode.CITED_ATTACHMENT_ABSENT]: "Upload the missing attachment to this contract's documents, or add the dates by hand. Then " + 'close this with what you did.',
    [ReviewReasonCode.LINE_SNAPSHOT_FROZEN]: 'Nothing was lost or changed: the lines on file stay as they are. Acknowledge this to ' + 'continue. If a line is wrong, use "Correct this line" in this review.'
};
export function nextStep(review) {
    return NEXT_STEP[review.reasonCode] ?? '';
}
export function isCheckOnly(review) {
    return !!review && CHECK_ONLY_CODES.has(review.reasonCode);
}
// An untargeted period date whose every period would be wrong (a completion
// date read as a period end): the honest answer is "none of these".
const NO_PERIOD_COPY = {
    label: 'Not a period date',
    toggle: 'None of these periods…',
    action: 'None of these',
    hint: 'Nothing is written to any period; the item stops blocking commit.',
    prompt: "Why doesn't this date belong to a period?",
    placeholder: 'For example, it is the ultimate completion date, not a period end.',
    settled: 'Not a period date. Nothing is written to any period.'
};
// An exercise whose task the modification doesn't settle may be no exercise
// at all (a clause read as one): the honest answer is "not an exercise".
const NOT_AN_EXERCISE_COPY = {
    label: 'Not an exercise',
    toggle: 'Not an exercise…',
    action: 'Not an exercise',
    hint: 'Nothing is marked exercised; the item stops blocking commit.',
    prompt: "Why isn't this an exercise?",
    placeholder: 'For example, the sentence restates an earlier exercise.',
    settled: 'Not an exercise. Nothing is marked exercised.'
};
export function blankCopy(review) {
    if (isCheckOnly(review)) return CHECKED_COPY;
    if (review?.reasonCode === ReviewReasonCode.POP_CHANGE_UNTARGETED) return NO_PERIOD_COPY;
    if (review?.reasonCode === ReviewReasonCode.OPTIONAL_TASK_EXERCISE_UNREAD) return NOT_AN_EXERCISE_COPY;
    if (review && isOccurrenceOwner(review)) return ONE_FILING_FOR_EVERY_TEAM_COPY;
    return review?.reasonCode === ReviewReasonCode.OPTION_PERIOD_FUNDED_NOT_EXERCISED ? FUNDED_NOT_EXERCISED_COPY : LEAVE_BLANK_COPY;
}
export function leftBlankRationale(reason, review) {
    return `${blankCopy(review).label}. ${reason.trim()}`;
}
// A resolution state as a reviewer reads it.
export function resolutionLabel(state, review) {
    return state === 'Waived' ? blankCopy(review).label : state;
}
// The toast after a decision is saved.
export function resolvedToast(state) {
    if (state === 'Waived') return 'Left blank';
    if (state === 'Rejected' || state === 'Escalated') return `Marked ${state.toLowerCase()}`;
    return 'Decision saved';
}
// Today in the reviewer's own time zone, as the YYYY-MM-DD a date input takes.
export function todayISO(now = new Date()) {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
function withNote(auto, note) {
    const n = note.trim();
    return n ? `${auto}. ${n}` : auto;
}
// The conflict candidate the document's own evidence recommends: the one
// candidate scored as recommended (0.9 and over) above every other. It is
// pre-selected, so the reviewer confirms it with one click; none otherwise.
export function recommendedCandidateKey(review) {
    if (review.targetFieldPath.trim() === 'scope_name') {
        // A line naming two efforts: the reading's answer (both, as printed)
        // leads, so Accept confirms it in one click.
        const read = review.valueJson.trim();
        return read && review.candidates.find((c)=>c.valueJson.trim() === read)?.key || '';
    }
    if (picksTarget(review) || picksTeam(review)) return '';
    const top = review.candidates.filter((c)=>c.score >= 0.9);
    return top.length === 1 ? top[0].key : '';
}
// Identity candidates carry a score; the reader sees words, never a number.
export function likelihood(score) {
    if (score >= 0.5) return 'likely';
    if (score > 0) return 'possible';
    return '';
}
// The lines a later document raised the same finding on, recorded against
// this item instead (D96): settling this item settles them.
export function affectedLines(review) {
    const out = [];
    for (const l of review.lineage){
        if (l.kind === ReviewLineageKind.AFFECTED && l.locator && !out.includes(l.locator)) {
            out.push(l.locator);
        }
    }
    return out;
}
export function gateNote(open) {
    return `Commit is off: ${open} fact${open === 1 ? '' : 's'} need${open === 1 ? 's' : ''} your call. ` + "The documents disagree or don't say which line a value belongs to, and nothing is " + 'applied until each is settled.';
}
// A period natural key ("base", "option:2") → that period's row id, which is
// what SetDiscrepancyTargetPeriod takes. Mirrors pangaea's parsePeriodNaturalKey.
export function periodIdForKey(key, periods) {
    const k = key.trim().toLowerCase();
    if (k === 'base') return periods.find((p)=>p.periodType === 'base')?.id ?? '';
    if (k.startsWith('option:')) {
        const n = Number(k.slice('option:'.length));
        return periods.find((p)=>p.periodType !== 'base' && p.optionNumber === n && Number.isInteger(n))?.id ?? '';
    }
    return periods.find((p)=>p.id === key)?.id ?? '';
}
// A line's CLIN number (or row id) → that current line's row id.
export function lineIdForKey(key, lines) {
    const k = key.trim().toUpperCase();
    return lines.find((l)=>l.clinNumber.trim().toUpperCase() === k || l.id === key)?.id ?? '';
}
// A task number (or row id) → that optional task's row id.
export function taskIdForKey(key, tasks) {
    const n = Number(key.trim());
    return tasks.find((t)=>Number.isInteger(n) && n > 0 && t.taskNumber === n || t.id === key)?.id ?? '';
}
// An identity candidate's value_json is the target's natural key as JSON.
export function candidateKey(c) {
    try {
        const v = JSON.parse(c.valueJson);
        if (typeof v === 'string') return v;
    } catch  {
    /* fall through */ }
    return c.valueJson || c.key;
}
// ---------------------------------------------------------------------------
// Decidable items: the question, its real choices, the evidence and what the
// decision writes.
// ---------------------------------------------------------------------------
// The contract types pangaea writes on a line (clinContractTypes in
// delivery_contract_clin_contract_type.go). Labor-hour is written as T&M
// there, so it is one choice. A value outside this list refuses at apply, so
// a line's type is picked, never typed.
export const CONTRACT_TYPE_CHOICES = [
    {
        value: 'FFP',
        label: 'Firm-fixed price (FFP)'
    },
    {
        value: 'T&M',
        label: 'Time and materials or labor-hour (T&M)'
    },
    {
        value: 'CR',
        label: 'Cost-reimbursable (CR)'
    },
    {
        value: 'CPFF',
        label: 'Cost-plus-fixed-fee (CPFF)'
    },
    {
        value: 'CPIF',
        label: 'Cost-plus-incentive-fee (CPIF)'
    },
    {
        value: 'CPAF',
        label: 'Cost-plus-award-fee (CPAF)'
    }
];
const CHOICE_PREFIX = 'choice:';
function isLineContractType(review) {
    return targetKind(review.targetObjectType) === 'line' && review.targetFieldPath.trim() === 'contract_type';
}
// Whether the field takes only a fixed set of values, so "Enter a different
// value" is never offered (a typed value outside the set would refuse).
// A SOW review item's answer is an assignment (SowAssignmentValue): a typed
// value is nothing its write can read, so it offers no free text. A due item's
// own rule is entered through "Enter the rule" (R5-4).
export function takesTypedValue(review) {
    return !hasClosedChoices(review) && review.targetObjectType.trim() !== 'DutyAssignment';
}
export function hasClosedChoices(review) {
    // A condition set's branches are every answer its writer takes (code 30).
    if (review.reasonCode === ReviewReasonCode.CONDITION_BRANCH_UNRESOLVED) return true;
    // M4: a clause reaches one of its candidate sections or none; its writer
    // refuses any other section.
    if (review.targetObjectType === 'DutyInheritance') return true;
    // R9-L: the rest of a section is owed or not; nothing typed.
    if (review.targetObjectType === 'SectionList') return true;
    // R10-E: an event-date conflict is one of its days, or left unlogged.
    if (review.targetObjectType === 'ContractEvent') return true;
    // SAD-980: a re-extract's line set is kept or used, nothing typed.
    if (review.reasonCode === ReviewReasonCode.AWARD_REREAD_LINE_SET) return true;
    // SAD-979: a re-extract's deliverable is the same or not, added or not.
    if (review.reasonCode === ReviewReasonCode.AWARD_REREAD_SAME_DELIVERABLE || review.reasonCode === ReviewReasonCode.AWARD_REREAD_NEW_DELIVERABLE) return true;
    return isLineContractType(review);
}
// An emitter's ISO day ("2030-12-30", quoted or not) as the reviewer reads a
// date: "30 Dec 2030".
export function readableDays(text) {
    return text.replace(/"?\b(\d{4}-\d{2}-\d{2})\b"?/g, (whole, day)=>formatReviewDay(day) ?? whole);
}
function jsonString(json) {
    try {
        const v = JSON.parse(json);
        return typeof v === 'string' ? v : '';
    } catch  {
        return '';
    }
}
// Every choice the reviewer has: the item's own candidates, then the rest of
// a closed field's values (each written by the same writer), so a line whose
// type no document states can still be typed in one click. A condition
// branch's "closed choices" are its own candidates only — there is no fixed
// catalog of branches to pad them with, so contract-type choices never
// belong here.
export function reviewChoices(review) {
    if (!isLineContractType(review)) return review.candidates;
    const have = new Set(review.candidates.map((c)=>jsonString(c.valueJson).toUpperCase()));
    const extra = CONTRACT_TYPE_CHOICES.filter((c)=>!have.has(c.value.toUpperCase())).map((c)=>new ReviewCandidate({
            key: `${CHOICE_PREFIX}${c.value}`,
            label: c.label,
            valueJson: JSON.stringify(c.value)
        }));
    return [
        ...review.candidates,
        ...extra
    ];
}
// The question the reviewer answers, naming the record. Empty for a finding
// with nothing to pick (it reads "What to do"). Optional choiceLabels name
// the real options on blocking cards (team questions especially).
export function reviewQuestion(review, record, choiceLabels = []) {
    const field = fieldLabel(review.targetFieldPath).toLowerCase();
    if (isCheckOnly(review) || review.reasonCode === ReviewReasonCode.LINE_SNAPSHOT_FROZEN) {
        return 'What to do';
    }
    const withChoices = (q)=>{
        if (choiceLabels.length === 0) return q;
        if (choiceLabels.length === 1) return `${q} Choose: ${choiceLabels[0]}`;
        if (choiceLabels.length === 2) {
            return `${q} Choose: ${choiceLabels[0]}, or ${choiceLabels[1]}`;
        }
        return `${q} Choose: ${choiceLabels.slice(0, -1).join('; ')}; or ${choiceLabels[choiceLabels.length - 1]}`;
    };
    const teamQ = teamQuestionTitle(review);
    if (teamQ) return withChoices(teamQ);
    if (picksTeam(review) || isOccurrenceOwner(review)) {
        return withChoices('Which team?');
    }
    switch(review.reasonCode){
        case ReviewReasonCode.CONTRACT_TYPE_CONFLICT:
            return `What contract type is ${record}?`;
        case ReviewReasonCode.DOCUMENT_STATES_TWO_VALUES:
            return `Which ${field} is right for ${record}?`;
        case ReviewReasonCode.POP_NOT_READ:
            return `What is the ${field} of ${record}?`;
        case ReviewReasonCode.MOD_EFFECTIVE_DATE_NOT_STATED:
            return `When did ${record} take effect?`;
        case ReviewReasonCode.MOD_PURPOSE_UNDER_GATE:
            return `Which sentence states the purpose of ${record}?`;
        case ReviewReasonCode.MOD_TYPE_UNDER_GATE:
            return `What type of modification is ${record}?`;
        case ReviewReasonCode.CONDITION_BRANCH_UNRESOLVED:
            return 'Which of these conditions holds?';
        case ReviewReasonCode.POP_CHANGE_UNTARGETED:
            return `Which period's ${field} is this?`;
        default:
            break;
    }
    if (review.kind === ReviewItemKind.IDENTITY) return 'Pick the line, task or period this belongs to';
    return review.targetFieldPath.trim() ? `What is the right ${field} for ${record}?` : 'Your call';
}
// A long run of short codes ("5001AA, 5001AB, … 5001BN") inside an emitter's
// sentence, collapsed to its ends and a count. full is the text unchanged.
const LIST_RUN = /\b[A-Z0-9][A-Z0-9.-]{1,11}(?:, (?:and )?[A-Z0-9][A-Z0-9.-]{1,11}){5,}\b/g;
export function compactReason(text) {
    let collapsed = false;
    const short = text.replace(LIST_RUN, (run)=>{
        const parts = run.split(/, (?:and )?/);
        collapsed = true;
        return `${parts[0]} through ${parts[parts.length - 1]} (${parts.length} in all)`;
    });
    return {
        short,
        full: text,
        collapsed
    };
}
// The sub-lines an emitter named in its reason ("It also covers 5001AA,
// 5001AB, …, which state no type of their own"): the fallback when the
// program's lines are not loaded.
export function subLinesFromReason(review) {
    const m = /It also covers (.+?), which state no type/.exec(review.reason);
    if (!m) return [];
    return m[1].split(/, (?:and )?/).map((s)=>s.trim()).filter(Boolean);
}
// The sub-lines a contract-type decision also writes: pangaea's writer types
// the line and every current sub-line under it that states no type of its own
// (applyContractCLINContractType). Read from the program's lines when they are
// loaded, so the count is what the writer will touch.
export function contractTypeSubLines(review, lines) {
    if (!isLineContractType(review)) return [];
    const parent = review.targetLocator.trim().toUpperCase();
    if (!parent) return [];
    if (lines.length > 0) {
        return lines.filter((l)=>(l.parentClinNumber ?? '').trim().toUpperCase() === parent).filter((l)=>{
            const t = (l.contractType ?? '').trim().toUpperCase();
            return t === '' || t === 'OTHER';
        }).map((l)=>l.clinNumber);
    }
    return subLinesFromReason(review);
}
// ---- Evidence ------------------------------------------------------------
const MONTHS_LONG = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];
// The ways a value is printed in a document: a date in words and numbers, an
// amount with its commas, a string as it is.
function printedForms(json) {
    let v;
    try {
        v = JSON.parse(json);
    } catch  {
        return [];
    }
    if (typeof v === 'number') {
        return [
            v.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
        ];
    }
    if (typeof v !== 'string' || !v.trim()) return [];
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
    if (!m) return v.length >= 3 ? [
        v.trim()
    ] : [];
    const month = MONTHS_LONG[Number(m[2]) - 1];
    if (!month) return [];
    const day = Number(m[3]);
    return [
        `${month} ${day}, ${m[1]}`,
        `${month.slice(0, 3)} ${day}, ${m[1]}`,
        `${Number(m[2])}/${day}/${m[1]}`
    ];
}
// The words that make a passage relevant to this item: its values as printed.
export function quoteNeedles(review, extraValues = []) {
    const values = [
        review.valueJson,
        ...review.candidates.map((c)=>c.valueJson),
        ...extraValues
    ].filter(Boolean);
    return [
        ...new Set(values.flatMap(printedForms))
    ];
}
const FOCUS_OVER = 280;
// A long passage cut to the sentences that hold the item's value, so the
// reviewer reads the line that matters ("Period of Performance (PoP) shall be
// September 23, 2025 through May 22, 2026.") and not the 600 characters around
// it. A short passage, or one no needle is in, is kept whole.
export function focusQuote(quote, needles) {
    if (quote.length <= FOCUS_OVER || needles.length === 0) return {
        text: quote,
        cut: false
    };
    const parts = quote.split(/(?<=[.;])\s+|\s*\|\s*|\n+/).filter((p)=>p.trim());
    const lower = needles.map((n)=>n.toLowerCase());
    const hit = parts.filter((p)=>lower.some((n)=>p.toLowerCase().includes(n)));
    if (hit.length === 0) return {
        text: quote,
        cut: false
    };
    const text = hit.join(' … ').trim();
    return {
        text,
        cut: text.length < quote.length
    };
}
function sameSource(a, b) {
    return a.page === b.page && a.quote.trim() === b.quote.trim();
}
// The item-level passages worth showing above the choices: each one not
// already shown under a choice. When the review carries none, the change
// item's own source reference (a modification's page and block) stands in.
export function itemSources(d, choices) {
    const r = d.review;
    const underChoices = choices.flatMap((c)=>c.sources);
    const own = r.sources.filter((s)=>!underChoices.some((u)=>sameSource(s, u)));
    if (r.sources.length > 0 || underChoices.length > 0) return own;
    const ref = reviewSourceProps(d, {
        after: '',
        before: ''
    });
    if (!ref.quote && !ref.page) return [];
    const { blockId, page } = parseSpan(ref.span);
    return [
        new ReviewSource({
            artifactId: ref.artifactId ?? '',
            page: ref.page || page || 0,
            quote: ref.quote ?? '',
            elementIds: blockId ? [
                blockId
            ] : []
        })
    ];
}
// What the evidence section says when there is no passage to show, instead
// of quoting something unrelated.
export function noEvidenceNote(review) {
    if (review.reasonCode === ReviewReasonCode.LINE_SNAPSHOT_FROZEN) {
        return 'Nothing to quote: this is about the import, not a passage in the document.';
    }
    if (review.reasonCode === ReviewReasonCode.DOCUMENT_STATES_TWO_VALUES) {
        return 'The two values are worked out from the lines below, not quoted from one passage.';
    }
    if (!disagrees(review) || isCheckOnly(review)) {
        return 'No passage in the document states this.';
    }
    return 'Specra recorded no passage for this.';
}
// Whether the evidence shown is where we looked rather than what was stated:
// a value no document states.
export function evidenceIsWhereWeLooked(review) {
    return review.kind === ReviewItemKind.CONFLICT && !disagrees(review) && (NOT_STATED_CODES.has(review.reasonCode) || review.reasonCode === ReviewReasonCode.CONTRACT_TYPE_CONFLICT);
}
// ---- What the decision writes ---------------------------------------------
// Whether the reviewer only acknowledges this item: pangaea records it and
// writes nothing (zou projects reviewitem.AcknowledgeOnly as acknowledges).
// A frozen line snapshot is acknowledge-only wherever it is raised.
export function acknowledgesOnly(d) {
    if (d.review.candidates.length > 0) return false;
    return !!d.acknowledges || d.review.reasonCode === ReviewReasonCode.LINE_SNAPSHOT_FROZEN;
}
// "Settled by P00001" from pangaea's lineage rationale, or "" when a reviewer
// decided it.
export function settledBy(rationale) {
    const m = /^settled by (.+)$/i.exec(rationale.trim());
    if (!m) return '';
    const by = m[1].trim();
    if (/^code\b/i.test(by)) return 'Specra';
    return by;
}
// The sentence under Decision: what is written, where, and when. Never a bare
// state: "Accepted: contract type FFP on CLIN 5001 and its 36 sub-lines".
export function decisionSummary(d, opts) {
    const r = d.review;
    const state = d.resolutionState;
    const applied = state === 'Applied';
    if (state === 'Waived') return blankCopy(r).settled;
    if (state === 'Acknowledged' || acknowledgesOnly(d) && SETTLED_STATES.has(state)) {
        return 'Acknowledged. Nothing is written.';
    }
    const value = d.decidedValueJson ?? '';
    const rationale = (d.decisionRationale ?? '').trim();
    // A later document's explicit change closed the question (D96, SAD-893).
    const superseded = /^superseded by (.+)$/i.exec(rationale);
    if (state === 'Closed' && superseded && !hasJsonValue(value)) {
        return `Superseded by ${superseded[1].trim()}, which restates it. Nothing is written for it.`;
    }
    // A later document reprinted the same value and closed the question (SAD-896).
    const confirmed = /^confirmed by (.+)$/i.exec(rationale);
    if (state === 'Closed' && confirmed && !hasJsonValue(value)) {
        return `Confirmed by ${confirmed[1].trim()}, which reprints it. Nothing is written for it.`;
    }
    const by = settledBy(d.decisionRationale ?? '');
    const head = by ? `Settled by ${by}` : applied ? 'Applied' : state;
    if (!hasJsonValue(value)) {
        if (applied) return 'Applied.';
        if (r.kind === ReviewItemKind.IDENTITY && d.decisionRationale) {
            return `${head}: ${d.decisionRationale}. Will apply when committed.`;
        }
        return `${head}, but no value was recorded, so nothing will be written for it.`;
    }
    const field = r.targetFieldPath.trim();
    // A line's window decided on its start field sets both ends.
    const window = field === 'pop_start_date' && displayValue(value, field).includes(' – ');
    const what = window ? `period of performance ${displayValue(value, field)}` : field ? `${fieldLabel(field).toLowerCase()} ${displayValue(value, field)}` : displayValue(value, field);
    const n = opts.subLines ?? 0;
    // One decision for several lines (also_target_locators): name them all.
    const also = r.alsoTargetLocators ?? [];
    const where = also.length > 0 ? ` on ${opts.record} and ${also.length} more line${also.length === 1 ? '' : 's'} (${also.join(', ')})` : n > 0 ? ` on ${opts.record} and its ${n} sub-line${n === 1 ? '' : 's'}` : ` on ${opts.record}`;
    const when = applied ? ' Written.' : ' Writes when this review is committed.';
    return `${head}: ${what}${where}.${when}`;
}
